import 'theme';

class KioskNosFilter extends HTMLElement {
  connectedCallback() {
    this.events = new AbortController();
    this.requests = new AbortController();
    queueMicrotask(() => this.initialize());
  }

  disconnectedCallback() {
    this.events?.abort();
    this.requests?.abort();
    this.observer?.disconnect();
    if (this.visibilityFrame) cancelAnimationFrame(this.visibilityFrame);
    this.section?.removeAttribute('data-kiosk-nos-active');
  }

  initialize() {
    if (!this.isConnected) return;
    this.section = this.closest('.shopify-section');
    this.toggle = this.querySelector('[data-nos-toggle]');
    this.ratioToggle = this.querySelector('[data-ratio-toggle]');
    if (!this.section || !this.toggle) return;
    this.hidden = false;
    const params = new URL(location.href).searchParams;
    this.excludeNos = params.get('exclude_nos') === '1';
    this.ratioOnly = params.get('availability_over_60') === '1';
    this.active = this.excludeNos || this.ratioOnly;
    this.toggle.checked = this.excludeNos;
    if (this.ratioToggle) this.ratioToggle.checked = this.ratioOnly;
    const options = { signal: this.events.signal };
    const onToggle = () => {
      const url = new URL(location.href);
      url.searchParams.delete('page');
      url.searchParams.delete('exclude_nos');
      url.searchParams.delete('availability_over_60');
      if (this.toggle.checked) url.searchParams.set('exclude_nos', '1');
      if (this.ratioToggle?.checked) url.searchParams.set('availability_over_60', '1');
      this.navigate(url);
    };
    this.toggle.addEventListener('change', onToggle, options);
    this.ratioToggle?.addEventListener('change', onToggle, options);
    document.addEventListener('facet:update', (event) => {
      if (event.detail?.url?.searchParams.get('section_id') === this.dataset.sectionId) {
        this.requests.abort();
        this.observer?.disconnect();
      }
    }, { ...options, capture: true });
    window.addEventListener('popstate', () => {
      this.navigate(new URL(location.href));
    }, options);
    this.section.querySelectorAll('[data-nos-form-value]').forEach((input) => {
      input.disabled = !this.excludeNos;
    });
    this.section.querySelectorAll('[data-ratio-form-value]').forEach((input) => {
      input.disabled = !this.ratioOnly;
    });
    if (!this.active) return;
    this.section.setAttribute('data-kiosk-nos-active', '');
    this.preserveFilterLinks();
    this.results = this.section.querySelector('[data-nos-results]');
    this.list = this.results?.querySelector('product-list');
    if (!this.list) {
      this.updateCounts(0);
      return;
    }
    this.pageSize = Number(this.results.dataset.pageSize) || 50;
    this.currentPage = Number(this.results.dataset.currentPage) || 1;
    if (this.currentPage > 1) {
      const url = new URL(location.href);
      url.searchParams.delete('page');
      this.navigate(url);
      return;
    }
    this.nextUrl = this.results.dataset.nextUrl || '';
    this.queue = [];
    this.seen = new Set();
    this.shown = 0;
    this.collect(this.list);
    this.list.replaceChildren();
    const footer = this.querySelector('[data-nos-footer-template]').content.cloneNode(true);
    this.results.append(footer);
    this.status = this.results.querySelector('[data-nos-status]');
    this.moreButton = this.results.querySelector('[data-nos-more]');
    this.sentinel = this.results.querySelector('[data-nos-sentinel]');
    this.moreButton.addEventListener('click', () => this.fill(), options);
    if (this.sentinel && 'IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) this.autoLoad();
      }, { rootMargin: '600px 0px' });
      this.observer.observe(this.sentinel);
    } else if (this.sentinel) {
      window.addEventListener('scroll', () => this.scheduleAutoLoad(), { ...options, passive: true });
      window.addEventListener('resize', () => this.scheduleAutoLoad(), options);
    }
    this.fill();
  }

  navigate(url) {
    url.searchParams.set('section_id', this.dataset.sectionId);
    this.dispatchEvent(new CustomEvent('facet:update', { bubbles: true, detail: { url } }));
  }

  preserveFilterLinks() {
    this.section.querySelectorAll('facet-link a[href]').forEach((link) => {
      if (link.matches('.facets-clear-all') || link.closest('.empty-state')) return;
      const url = new URL(link.href, location.href);
      if (url.origin !== location.origin || url.pathname !== location.pathname) return;
      if (this.excludeNos) url.searchParams.set('exclude_nos', '1');
      else url.searchParams.delete('exclude_nos');
      if (this.ratioOnly) url.searchParams.set('availability_over_60', '1');
      else url.searchParams.delete('availability_over_60');
      link.href = url.href;
    });
  }

  collect(list) {
    for (const card of list.querySelectorAll(':scope > product-card')) {
      const handle = card.getAttribute('handle');
      if (!handle || this.seen.has(handle)) continue;
      if (this.excludeNos && card.dataset.kioskNos !== 'false') continue;
      if (this.ratioOnly) {
        const available = Number(card.dataset.kioskAvailableVariants);
        const total = Number(card.dataset.kioskTotalVariants);
        if (!Number.isInteger(available) || !Number.isInteger(total) || total < 1 || available < 0 || available > total || available * 100 <= total * 60) continue;
      }
      this.seen.add(handle);
      const copy = document.importNode(card, true);
      copy.removeAttribute('reveal-on-scroll');
      copy.style.removeProperty('opacity');
      copy.style.removeProperty('transform');
      this.queue.push(copy);
    }
  }

  appendUntil(target) {
    const fragment = document.createDocumentFragment();
    while (this.queue.length && this.shown < target) {
      fragment.append(this.queue.shift());
      this.shown += 1;
    }
    this.list.append(fragment);
    this.updateCounts(this.shown);
  }

  async nextPage() {
    const sourceUrl = new URL(this.nextUrl, location.href);
    const nextPage = Number(sourceUrl.searchParams.get('page'));
    if (sourceUrl.origin !== location.origin || !Number.isInteger(nextPage) || nextPage <= this.currentPage) {
      throw new Error('Invalid collection page');
    }
    const url = new URL(location.href);
    url.searchParams.set('page', String(nextPage));
    url.searchParams.set('section_id', this.dataset.sectionId);
    const response = await fetch(url.href, { signal: this.requests.signal, credentials: 'same-origin' });
    if (!response.ok) throw new Error('Collection request failed');
    const html = await response.text();
    if (this.requests.signal.aborted || !this.isConnected) return;
    const parsed = new DOMParser().parseFromString(html, 'text/html');
    const results = parsed.querySelector('[data-nos-results]');
    const list = results?.querySelector('product-list');
    if (!list || Number(results.dataset.currentPage) !== nextPage) {
      throw new Error('Collection page unavailable');
    }
    this.collect(list);
    this.currentPage = nextPage;
    this.nextUrl = results.dataset.nextUrl || '';
  }

  async fill() {
    if (this.busy || this.requests.signal.aborted) return;
    this.busy = true;
    const target = this.failed ? this.pendingTarget : this.shown + this.pageSize;
    this.pendingTarget = target;
    this.failed = false;
    this.moreButton.disabled = true;
    this.status.textContent = this.dataset.loadingLabel;
    this.list.setAttribute('aria-busy', 'true');
    try {
      this.appendUntil(target);
      while (this.shown < target && this.nextUrl && !this.requests.signal.aborted && this.isConnected) {
        await this.nextPage();
        if (this.requests.signal.aborted || !this.isConnected) return;
        this.appendUntil(target);
      }
      this.status.textContent = this.shown ? this.countLabel(this.shown) : this.dataset.emptyLabel;
    } catch (error) {
      if (error.name === 'AbortError') return;
      this.failed = true;
      this.status.textContent = this.dataset.errorLabel;
    } finally {
      this.busy = false;
      if (this.isConnected && !this.requests.signal.aborted) {
        this.list.removeAttribute('aria-busy');
        this.moreButton.disabled = false;
        this.moreButton.hidden = !this.failed && !this.queue.length && !this.nextUrl;
        this.moreButton.textContent = this.failed ? this.dataset.retryLabel : this.dataset.moreLabel;
        if (this.moreButton.hidden) this.observer?.disconnect();
        else if (!this.failed) this.scheduleAutoLoad();
      }
    }
  }

  scheduleAutoLoad() {
    if (this.visibilityFrame || !this.sentinel) return;
    this.visibilityFrame = requestAnimationFrame(() => {
      this.visibilityFrame = null;
      if (!this.isConnected || this.requests.signal.aborted) return;
      const bounds = this.sentinel.getBoundingClientRect();
      if (bounds.top <= window.innerHeight + 600 && bounds.bottom >= -600) this.autoLoad();
    });
  }

  autoLoad() {
    if (!this.busy && !this.failed && !this.requests.signal.aborted && (this.queue.length || this.nextUrl)) this.fill();
  }

  countLabel(count) {
    return this.dataset.countLabel.replace('__count__', new Intl.NumberFormat(document.documentElement.lang || 'en').format(count));
  }

  updateCounts(count) {
    this.section.querySelectorAll('[data-nos-product-count]').forEach((node) => {
      node.textContent = this.countLabel(count);
    });
  }
}

if (!customElements.get('kiosk-nos-filter')) {
  customElements.define('kiosk-nos-filter', KioskNosFilter);
}
